import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Indonesian translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep it in step with en.tsx.
 */
export function GuideId({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Cara kerja Vrelis</h1>
      <p className="lead">
        Vrelis adalah layanan untuk mengirim pesan terenkripsi, kapsul waktu, yang hanya dapat dibaca mulai saat yang Anda pilih: oleh semua anggota uji coba, atau oleh
        satu orang. Sebelum saat itu tidak ada yang dapat membaca kapsul: baik orang yang Anda tuju, orang yang menjalankan Vrelis, maupun Anda sendiri.
      </p>
      <p className="warn">
        Ini adalah uji coba tertutup. Unduh kapsul yang ingin Anda simpan (lihat di bawah), dan simpan frasa pemulihan Anda.
      </p>

      <h2>Mengapa kunci ini dapat dipercaya</h2>
      <p>
        Kunci ini bukan janji dari Vrelis. Kunci ini adalah kriptografi yang dipublikasikan ditambah sinyal waktu publik yang tidak dikendalikan Vrelis. Berikut yang menopangnya,
        dan apa yang harus Anda yakini agar kunci ini gagal.
      </p>

      <h3>drand: jam publik yang menandatangani waktu</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> adalah jaringan yang dijalankan oleh{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, sekelompok organisasi independen (di antaranya Cloudflare, Protocol Labs, Kudelski
          Security, EPFL, dan University College London) yang bersama-sama menerbitkan nilai acak yang ditandatangani setiap 3 detik. Nilai-nilai itu bernomor: putaran 1,
          putaran 2, dan seterusnya, masing-masing untuk saat yang diketahui. Siapa pun dapat membacanya dari beberapa relay publik; tidak ada yang dapat membaca nilai masa depan, karena nilai itu belum
          ada.
        </li>
        <li>
          Setiap putaran ditandatangani dengan kriptografi ambang (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">tanda tangan BLS</Ext>): setiap organisasi memegang satu bagian dari kunci rahasia, dan sebuah tanda tangan memerlukan
          mayoritas bagian itu. Tidak ada satu organisasi pun, dan tidak ada kelompok minoritas, yang dapat membuat tanda tangan sebuah putaran sebelum waktunya. Jaringan ini berjalan
          sejak 2020; rantai yang kami pakai,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, sejak 2023.
          Kunci publiknya tertanam tetap di dalam kode kami, dan setiap sinyal yang kami terima diperiksa terhadapnya.
        </li>
        <li>
          <b>Enkripsi kunci waktu</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, dipublikasikan oleh peneliti dari Protocol Labs dan Cloudflare
          pada 2023) mengubah jam itu menjadi kunci: sebuah pesan
          dienkripsi ke nomor putaran, dan tanda tangan putaran itu, setelah diterbitkan, adalah satu-satunya kunci yang membukanya. Sampai drand menerbitkannya, kunci itu tidak ada
          di mana pun di dunia.
        </li>
      </ul>

      <h3>Cara Vrelis memakainya</h3>
      <ul>
        <li>
          Browser Anda mengenkripsi teks dan kunci file Anda ke putaran drand pada waktu yang Anda pilih; Tulis menampilkan nomor putaran sebelum Anda mengirim.
          Kapsul pribadi dienkripsi sekali lagi ke kunci penerima, sehingga setelah waktunya hanya penerima yang dapat membukanya.
        </li>
        <li>
          Server kami hanya menerima hasil yang sudah tersegel dan menyimpannya. Server tidak memegang kunci apa pun yang membuka kapsul: tidak sebelum waktunya, dan untuk kapsul pribadi
          juga tidak sesudahnya. Kebocoran basis data akan membuka ciphertext dan metadata, tidak pernah isi pesan.
        </li>
        <li>
          Saat waktunya tiba, browser Anda (atau pembuka offline) mengambil tanda tangan putaran dari salah satu dari empat relay drand publik, memverifikasinya
          terhadap kunci publik yang tetap, lalu mendekripsi. Vrelis tidak berperan dalam langkah itu.
        </li>
        <li>
          Setiap kapsul ditandatangani dengan kunci penulisnya (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, jenis kunci yang sama dengan yang dipakai Bitcoin
          dan <Ext href="https://nostr.com">Nostr</Ext>), sehingga kapsul yang diubah akan terdeteksi. Kunci dibuat di browser Anda dari 12 kata Anda (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) dan tidak pernah keluar dari browser tanpa enkripsi.
        </li>
      </ul>

      <h3>Jika Vrelis menghilang</h3>
      <ul>
        <li>
          Formatnya terbuka: kapsul adalah <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">event Nostr</Ext> (sebuah{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">gift wrap NIP-59</Ext> untuk kapsul pribadi) yang isinya adalah file{" "}
          <Ext href="https://age-encryption.org">age</Ext> dengan stanza <Ext href="https://github.com/drand/tlock-js">tlock</Ext>. Perangkat lunak apa pun yang
          menerapkan spesifikasi terbuka ini dapat membuka kapsul Anda; perangkat lunak kami tidak wajib.
        </li>
        <li>
          <b>Unduh</b> menyimpan kapsul di disk Anda sendiri, bersama pembuka offline: satu file HTML yang membukanya di komputer Anda, tanpa
          server Vrelis, dengan sinyal drand yang diperlukan dan, untuk kapsul pribadi, kunci Anda. Ekspor lengkap dapat dibuka tanpa vrelis.com jika
          sinyal yang diperlukan tersedia dan Anda menyimpan file serta kunci yang dibutuhkannya.
        </li>
        <li>
          Jika drand sendiri suatu saat berhenti sebelum menghasilkan sinyal masa depan yang diperlukan, kapsul-kapsul itu dapat tetap terkunci selamanya. Mengunduhnya
          lebih awal tidak menghilangkan risiko itu. Kapsul yang tanggal pembukaannya sudah lewat tetap memerlukan sinyal persisnya yang sudah diverifikasi: tanpa sinyal itu di
          arsip Anda atau dari relay, tanggal saja tidak memungkinkan Anda membukanya secara offline. Simpan ekspor yang lengkap dan kunci yang dibutuhkannya.
        </li>
      </ul>

      <h3>Apa yang harus Anda yakini agar kunci ini gagal</h3>
      <ul>
        <li>
          Bahwa mayoritas organisasi drand bersekongkol untuk menandatangani sebuah putaran lebih awal. Mereka independen, berada di beberapa negara, dan rantainya publik, sehingga
          tanda tangan seperti itu akan terlihat oleh semua orang. Inilah asumsi kepercayaan seluruh sistem.
        </li>
        <li>
          Bahwa halaman web berbohong. Halaman itu adalah kode yang dikirim Vrelis ke browser Anda; siapa pun yang mengendalikan kode itu dapat membaca teks sebelum disegel, atau
          kunci Anda setelah Anda membukanya. Karena itulah kapsul dapat diunduh dan dibuka secara offline, sidik jari kunci ditampilkan agar Anda dapat membandingkannya di luar
          Vrelis, dan aplikasi ponsel yang menjauhkan kunci dari halaman web direncanakan setelah uji coba.
        </li>
        <li>
          Bahwa kriptografi masa kini dibobol. Kriptografi kurva eliptik, yang dipakai drand maupun kunci Anda, mungkin dapat dibobol oleh{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">komputer kuantum</Ext> di masa depan.
          Untuk kapsul yang dimaksudkan tetap tersegel selama puluhan tahun, ini adalah risiko nyata yang tidak diatasi oleh uji coba.
        </li>
      </ul>

      <h2>Cara bergabung</h2>
      <ol>
        <li>
          Jika administrator sudah menyetujui alamat Anda, daftarlah langsung dengan alamat itu; jika belum, buka tautan undangan Anda dan gunakan alamatnya. Masuk dengan akun Google itu (Google hanya memberi tahu Vrelis
          nama dan email Anda), atau dengan kata sandi (kami mengirim tautan lewat email untuk mengonfirmasi alamat). Nanti Anda dapat menambahkan passkey untuk masuk.
        </li>
        <li>
          Vrelis menawarkan untuk membuat <b>kunci</b> Anda di browser: tanda tangan utama Anda, yang menandatangani apa yang Anda kirim dan membuka kapsul pribadi yang dikirim kepada Anda. Anda
          dapat melewatinya dan membuatnya nanti di Akun, Kunci; tanpa kunci Anda hanya dapat membaca kapsul publik. Sebelum <b>frasa pemulihan</b> Anda (12 kata yang
          membangun ulang kunci di perangkat mana pun) muncul, Anda diminta memastikan tidak ada yang dapat melihat atau merekam layar Anda.
        </li>
        <li>Tulis 12 kata itu di kertas. Anda diminta mengetik ulang tiga di antaranya, lalu masuk lagi untuk mengonfirmasi bahwa ini Anda: dengan kata sandi atau passkey, atau dengan Google untuk kunci pertama Anda.</li>
        <li>
          Pilih PIN (minimal {l.pinDigits} angka) dan buat <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">passkey</Ext> (sidik jari,
          wajah, atau PIN perangkat Anda). Bersama-sama keduanya melindungi kunci di perangkat ini
          dan salinan yang disimpan Vrelis untuk pindah ke perangkat baru. Jika browser Anda tidak dapat memakai passkey dengan cara ini, kunci tetap tidak terlindungi di browser itu dan
          tidak ada salinan di server; frasa lalu menjadi satu-satunya jalan ke perangkat lain.
        </li>
      </ol>

      <h2>Frasa pemulihan Anda</h2>
      <ul>
        <li>Simpan di kertas, di tempat yang aman. Jangan memotretnya dan jangan menyimpannya di komputer atau di pengelola kata sandi.</li>
        <li>
          Siapa pun yang memiliki 12 kata itu <b>adalah</b> Anda: mereka dapat menandatangani sebagai Anda dan membuka kapsul pribadi Anda. Jika kata-kata itu bocor, gunakan <b>Reset kunci</b> di Akun,
          Kunci: Anda mengetik frasa, masuk lagi, dan kunci dicabut untuk selamanya; tidak ada yang dapat mengirim dengan kunci itu atau ke kunci itu lagi. Anda menyebutkan sejak kapan kunci itu mungkin
          dicuri, dan kapsul yang dikirim dengannya sejak saat itu ditandai "kunci dicabut". Lalu Anda membuat kunci baru dengan frasa baru.
        </li>
        <li>
          Kunci-kunci Anda sebelumnya dapat dimasukkan ke <b>gantungan kunci</b>, dienkripsi dengan kunci Anda saat ini, sehingga kapsul yang dikirim ke kunci lama tetap terbuka tanpa frasa lama.
          Anda juga dapat menyimpan frasa lama sendiri dan menambahkannya ke gantungan kunci nanti.
        </li>
        <li>
          Jika Anda kehilangan perangkat: masuk di perangkat lain dan gunakan passkey dan PIN (jika passkey Anda tersinkron) atau frasa Anda. Jika Anda lupa PIN: gunakan
          frasa. Setelah {l.pinAttempts} PIN salah, salinan di server diblokir; frasa menulis salinan baru.
        </li>
        <li>
          Jika Anda kehilangan frasa <b>dan</b> setiap perangkat yang menyimpan kunci Anda, kapsul pribadi Anda tidak akan pernah dapat dibuka, oleh siapa pun. Admin hanya dapat memberi Anda
          identitas baru. Mencabut kunci juga memerlukan frasa, jadi tanpa frasa itu admin yang mencabutnya untuk Anda.
        </li>
      </ul>

      <h2>Menulis kapsul</h2>
      <ul>
        <li>
          <b>Semua anggota uji coba</b> (publik) atau <b>satu anggota</b> (pribadi). Kapsul publik dapat dibaca oleh setiap anggota setelah waktunya; kapsul pribadi hanya
          oleh orang yang Anda tuju, dan oleh Anda jika Anda menyimpan salinan (aktif secara bawaan).
        </li>
        <li>
          Setiap kapsul memerlukan <b>subjek</b>. Subjek tidak pernah dienkripsi: siapa pun yang melihat kapsul langsung membacanya, begitu juga server kami.
        </li>
        <li>
          <b>Pengantar</b> bersifat opsional: hingga {l.introChars} karakter yang dapat dibaca sebelum waktunya, saat kapsul diklik. Pada kapsul publik
          semua orang membacanya, termasuk server kami. Pada kapsul pribadi hanya penerima yang membacanya, dengan kuncinya; server tidak melihatnya. Pesan dan
          file-filenya tetap terkunci dalam kedua kasus.
        </li>
        <li>
          Siapa pengirim kapsul pribadi disegel untuk kunci penerima: penerima melihatnya begitu membuka kapsul dengan kuncinya, jauh sebelum waktunya.
          Siapa pun yang memegang kapsul tanpa kunci itu hanya melihat subjek dan waktunya. Server kami tahu akun mana yang mengirimnya. Server hanya memberi tahu penerima, terenkripsi untuk kuncinya, agar aplikasi dapat memeriksa bahwa kunci yang menandatangani kapsul memang milik akun itu; jika tidak, aplikasi menampilkan "Pengirim belum terverifikasi" sebagai ganti nama.
        </li>
        <li>
          <b>Samarkan pengirim</b> (kapsul pribadi): daftar penerima menampilkan "Pengirim disamarkan" sebagai ganti nama Anda sampai mereka mengklik kapsul, sehingga tidak ada orang
          yang mengintip dari balik bahu mereka yang tahu siapa yang menulis kepada mereka. Penerima tetap dapat melihat bahwa itu Anda, sebelum waktunya.
        </li>
        <li>
          Pilih kapan kapsul dibuka: dalam 5 menit, satu jam, satu hari, satu minggu, satu tahun, atau tanggal hingga tahun 2125. Waktu pembukaan sebenarnya ditampilkan sebelum Anda mengirim;
          ketepatannya sampai beberapa detik.
        </li>
        <li>
          Teks: hingga {l.publicText} untuk kapsul publik, {l.privateText} untuk kapsul pribadi. File: hingga {l.files} file dan, secara total, {l.fileBytes} pada Free atau {l.planFileBytes} dengan Legacy atau Start. Foto
          diperkecil dan metadatanya, termasuk lokasi, dihapus.
        </li>
        <li>
          <b>Segel dan kirim</b> meminta PIN dan passkey Anda sekali, untuk menandatangani kapsul. Setelah itu tidak ada yang dapat mengubahnya, dan tidak ada yang dapat membacanya sebelum
          waktunya.
        </li>
        <li>
          Sebelum menulis kepada seseorang secara pribadi, periksa kuncinya: Tulis menampilkan sidik jarinya (16 karakter). Jika kuncinya berubah sejak terakhir
          dilihat browser ini, Tulis memperingatkan Anda dan menunggu sampai Anda memastikan bersama orang itu, di luar Vrelis, bahwa kunci baru itu memang miliknya.
        </li>
      </ul>

      <h2>Membaca</h2>
      <ul>
        <li>
          <b>Linimasa</b>: kapsul publik. <b>Kotak masuk</b>: kapsul pribadi untuk Anda. <b>Terkirim</b>: kapsul Anda. <b>Dipantau</b>: kapsul publik yang Anda tunggu. Ikuti seorang
          penulis untuk melihat kapsulnya saja.
        </li>
        <li>
          <b>Sembunyikan</b> anggota: kapsul publiknya keluar dari Linimasa Anda, di setiap perangkat; tidak ada yang berubah baginya. <b>Blokir</b> anggota: ia tidak dapat
          lagi memasukkan kapsul pribadi ke Kotak masuk Anda (Tulis memberitahunya sebelum ia mengirim); Anda tetap dapat mengikutinya dan membaca kapsul publiknya.
          Kapsulnya yang lebih awal pindah ke Kotak masuk, Diblokir, dan tetap dapat dibuka. Keduanya ada di menu seseorang dan di halaman Orang, bersama siapa yang mengikuti Anda.
          Vrelis hanya menjamin apa yang diterima dan dikirimkannya: tidak ada yang dapat mencegah seseorang mengenkripsi ke kunci publik Anda di luar Vrelis.
        </li>
        <li>
          Kapsul pribadi tidak menampilkan apa pun sampai Anda membukanya: <b>Buka</b> meminta PIN Anda, lalu menampilkan siapa pengirimnya dan kapan kapsul dibuka, dan teksnya setelah waktu itu
          tiba.
        </li>
        <li>
          Kunci Anda tetap terkunci; setiap tindakan yang memerlukannya meminta PIN. Untuk membaca beberapa kapsul berturut-turut, gunakan <b>Buka kunci selama … menit</b> di bilah
          bagian atas. <b>Kunci</b> menyembunyikan semua yang pribadi sekaligus; meninggalkan halaman juga begitu.
        </li>
        <li>
          Di <a href="/account">Akun</a>, Privasi: "Tampilkan pengirim di daftar saya hanya setelah saya mengklik kapsul" memperlakukan setiap kapsul pribadi sebagai disamarkan,
          di setiap perangkat.
        </li>
        <li>
          Daftar menampilkan 50 kapsul sekaligus; <b>Tampilkan lagi</b> menambahkan kapsul berikutnya. Vrelis memfilter dan mengurutkannya (menurut waktu pembukaan atau menurut kapan Vrelis menerimanya),
          sehingga filter atau grup mengirimkan ke Vrelis ID anggota yang Anda pilih, tidak pernah grup atau namanya. Memfilter Kotak masuk menurut pengirim memerlukan
          kunci Anda, tidak menyertakan kapsul yang disamarkan, dan nonaktif selama pengirim hanya ditampilkan setelah diklik.
        </li>
        <li>
          <b>Cari</b> melihat subjek, pengantar kapsul publik, siapa yang menulis kapsul publik, dan kepada siapa Anda mengirim. Pencarian tidak pernah melihat teks yang sudah dibuka
          (hanya perangkat Anda yang memilikinya) atau siapa yang mengirimi Anda kapsul pribadi.
        </li>
      </ul>

      <h2>Amplop balasan, CC, dan Korespondensi</h2>
      <ul>
        <li>
          Pesan pribadi dapat membawa <b>amplop balasan</b>: Anda meminta setiap penerima mengirim satu balasan pribadi kepada Anda, tersegel untuk kunci Anda.
          Mereka dapat membalas meskipun tidak mengikuti Anda. Anda memilih apakah setiap balasan dibuka pada waktu yang Anda tetapkan (setelah waktu itu balasan tidak
          diterima lagi) atau pada waktu yang dipilih penerima. Anda membayar kapsul dan balasannya di muka: 2 perangko per penerima alih-alih 1. Balasan pertama penerima tidak membebaninya apa pun; balasan kedua membebaninya satu perangko, dan Anda melihat keduanya.
        </li>
        <li>
          <b>CC</b>: pesan pribadi untuk satu anggota dapat sekaligus dikirim ke anggota lain, dan setiap orang dalam daftar melihat siapa lagi yang menerimanya. Daftarnya
          disegel di dalam pesan dengan tanda tangan pengirim; server kami tidak pernah melihatnya. Pengiriman grup tanpa CC tetap tertutup: tidak ada yang melihat
          siapa lagi yang menerimanya.
        </li>
        <li>
          Pesan dengan amplop balasan dan balasannya ada di halaman <b>Korespondensi</b>, bukan di Kotak masuk atau Terkirim: di bawah setiap
          permintaan Anda melihat balasan setiap penerima secara berurutan, terkunci sampai waktunya, dan siapa yang membayar masing-masing. Balasan hanya dikirim kepada pengirim.
        </li>
      </ul>

      <h2>Menyimpan kapsul tanpa Vrelis</h2>
      <ul>
        <li>
          <b>Unduh</b> pada kapsul menyimpannya sebagai file; dengan lampiran, hasilnya berupa .zip berisi file-file dan pembuka offline. Pilih beberapa kapsul dan gunakan{" "}
          <b>Unduh yang dipilih</b> untuk satu arsip ("Pilih semua" mengambil kapsul yang ditampilkan).
        </li>
        <li>
          Pembuka offline adalah satu file HTML (<a href="/account">Akun</a>, atau di dalam .zip). File ini membuka kapsul Anda di komputer Anda sendiri, kapsul pribadi dengan frasa Anda. File ini
          tidak pernah menghubungi vrelis.com; file ini hanya meminta sinyal pembukaan dari layanan waktu drand publik jika file kapsul belum memuatnya.
        </li>
        <li>
          "Sertakan teks dan file yang sudah dibuka" memasukkan teks dan file yang sudah dibuka ke unduhan tanpa enkripsi: siapa pun yang memiliki file itu dapat membacanya.
        </li>
      </ul>

      <h2>Perangkat ini</h2>
      <ul>
        <li>
          <b>Kunci</b>: menghapus kunci dari memori. <b>Keluar</b>: mengakhiri sesi ini; kunci tetap di perangkat ini. <b>Keluar di semua perangkat</b>:
          mengakhiri sesi Anda di setiap perangkat. <b>Lupakan perangkat ini</b>: menghapus kunci Anda dan semua yang disimpan Vrelis di browser ini.
        </li>
        <li>Login berlaku selama {l.sessionDays} hari.</li>
      </ul>

      <h2>Apa yang dapat dan tidak dapat dilihat Vrelis</h2>
      <ul>
        <li>
          <b>Vrelis tidak dapat membaca</b> kapsul sebelum waktunya, atau kapsul pribadi kapan pun (Vrelis tidak punya kuncinya), atau kunci, PIN, maupun frasa Anda.
        </li>
        <li>
          <b>Vrelis memang melihat</b>: nama dan email Anda (pemilik dan para admin melihat email), siapa yang mengirim kapsul dan kepada siapa, kapan kapsul dikirim, kapan kapsul publik
          dibuka, ukurannya, dan bahwa kapsul memiliki file. Setelah waktunya, kapsul publik dapat dibaca oleh setiap anggota, jadi juga oleh Vrelis.
        </li>
        <li>
          Gantungan kunci dan salinan kunci Anda hanya disimpan dalam keadaan terenkripsi; Vrelis tidak dapat membukanya.
        </li>
        <li>
          Tanda tangan membuktikan kunci mana yang menulis kapsul. Kapsul bertanda "kunci dicabut" ditandatangani dengan kunci yang kemudian dilaporkan dicuri oleh pemiliknya. Bahwa kunci itu milik seseorang bergantung pada daftar anggota dan pada nama yang dimasukkan pengirim ke dalam
          kapsul. Periksa sidik jari bersama orang yang Anda kirimi kapsul pribadi.
        </li>
      </ul>

      <h2>Paket dan perangko</h2>
      <ul>
        <li>
          Satu <b>perangko</b> membayar satu pengiriman ke satu penerima. Salinan untuk Anda sendiri gratis. Free memberi {l.freeStamps} perangko per bulan, penyimpanan {l.freeStorage}, dan file {l.fileBytes} per pesan; Legacy
          ({l.legacyStamps} perangko per bulan, {l.planStorage}, {l.planFileBytes}) diberikan kepada anggota pertama; Start akan memberi {l.planStamps} perangko per bulan. Perangko suatu bulan tidak terbawa ke bulan berikutnya, dan perangko diisi kembali pada
          hari pertama bulan berikutnya (UTC). <b>Tagihan</b> menampilkan apa yang Anda miliki, apa yang tersisa, dan apa yang direncanakan.
        </li>
        <li>
          Untuk saat ini tidak ada yang ditagih dan tidak ada yang dapat dibeli: paket-paket ini masih pratinjau, dan admin mengaktifkan permintaan secara manual. Apa yang sudah Anda kirim tetap dapat dibaca oleh Anda dan oleh orang yang Anda kirimi, apa pun
          yang terjadi pada paket atau perangko Anda.
        </li>
        <li>
          Salinan untuk diri sendiri bersifat opsional dan aktif secara bawaan. Jika Anda menonaktifkannya, hanya penerima yang dapat membaca pesan itu: Anda sendiri tidak akan dapat membacanya, bahkan setelah pesan itu dibuka.
        </li>
      </ul>

      <h2>Batas uji coba</h2>
      <ul>
        <li>
          Maksimal {l.daily} pengiriman per hari (salinan untuk Anda sendiri gratis; kapsul untuk grup dihitung satu per anggota). Unggahan: {l.uploadDaily} dan {l.unusedFiles} file yang tidak terpakai per 24 jam;
          file yang tidak dipakai dalam kapsul dalam 24 jam akan dihapus.
        </li>
        <li>Masuk dengan Google, kata sandi, atau passkey. Tanpa pembayaran, tanpa jaringan publik: kapsul tetap di dalam uji coba.</li>
        <li>
          Mengundang alamat yang sudah punya akun akan mengirimkan permintaan terhubung kepada anggota itu; jika ia menerima, kalian saling mengikuti. Tampilan
          layar Anda sama dalam kedua kasus, sehingga tidak ada yang dapat memakai undangan untuk mengetahui siapa yang menjadi anggota.
        </li>
      </ul>

      <h2>Untuk pengembang</h2>
      <p>
        Layanan dapat membaca dan mengirim kapsul melalui <a href="/developers">API Vrelis</a>. Layanan mendapat akunnya sendiri melalui undangan layanan dari
        admin; bagi semua orang lain, layanan itu adalah anggota biasa. Layanan membuat kuncinya sendiri dan menandatangani setiap kapsul sendiri; token API-nya dapat membaca dan mengirim, tetapi
        tidak dapat mengelola kunci atau metode masuk. Anda dapat memblokir atau menyembunyikan layanan seperti anggota lainnya.
      </p>

      <h2>Yang kami minta dari anggota uji coba</h2>
      <ol>
        <li>Kirim satu kapsul publik yang dibuka dalam satu jam, dan satu kapsul pribadi kepada anggota lain yang dibuka dalam satu hari.</li>
        <li>Baca kapsul publik setelah dibuka, dan coba baca satu sebelum waktunya: Anda seharusnya tidak bisa.</li>
        <li>Salah satu dari Anda: siapkan kunci Anda di perangkat kedua dan baca Kotak masuk Anda di sana.</li>
        <li>Salah satu dari Anda: sembunyikan anggota lain dan periksa bahwa kapsulnya hanya hilang dari Linimasa Anda.</li>
      </ol>

      <h2>Masalah</h2>
      <p>
        Gunakan <b>Laporkan masalah</b> di bagian bawah setiap halaman, atau tulis ke {link}. Sebutkan apa yang Anda lakukan, apa yang Anda harapkan, dan apa yang terjadi; tangkapan layar akan membantu.
        Layanan, batas, dan tanggung jawabnya dijelaskan dalam <a href="/terms">Ketentuan penggunaan</a>.
      </p>
    </>
  );
}
